import React from 'react';
import { Card, CardHeader, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useSubscription } from '../../hooks/useSubscription';
import { CheckCircle, XCircle, Crown, Calendar, Users } from 'lucide-react';

const SubscriptionCard = ({ onUpgrade }) => {
  const { subscription, loading, error } = useSubscription();

  if (loading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </CardHeader>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full border-red-200">
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2 text-red-600">
            <XCircle className="h-5 w-5" />
            <span>Error loading subscription: {error}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!subscription) {
    return (
      <Card className="w-full">
        <CardContent className="pt-6">
          <div className="text-center text-gray-500">
            No subscription information available
          </div>
        </CardContent>
      </Card>
    );
  }

  const isFree = subscription.tier_name === 'free';
  const isPro = subscription.tier_name === 'pro';
  const isActive = subscription.status === 'active';

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {isPro && <Crown className="h-5 w-5 text-yellow-500" />}
            <h3 className="text-lg font-semibold">{subscription.tier_display_name}</h3>
            <Badge variant={isActive ? 'default' : 'secondary'}>
              {subscription.status}
            </Badge>
          </div>
          {isFree && (
            <Button onClick={onUpgrade} className="bg-blue-600 hover:bg-blue-700">
              <Crown className="h-4 w-4 mr-2" />
              Upgrade to Pro
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Usage Limits */}
        <div className="space-y-3">
          <h4 className="font-medium text-sm text-gray-700">Usage Limits</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="flex items-center space-x-2">
              <Users className="h-4 w-4 text-gray-500" />
              <span className="text-sm">
                {subscription.max_project_groups === -1 ? 'Unlimited' : subscription.max_project_groups} project groups
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Users className="h-4 w-4 text-gray-500" />
              <span className="text-sm">
                {subscription.max_projects_per_group === -1 ? 'Unlimited' : subscription.max_projects_per_group} projects per group
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Users className="h-4 w-4 text-gray-500" />
              <span className="text-sm">
                {subscription.max_tasks_per_project === -1 ? 'Unlimited' : subscription.max_tasks_per_project} tasks per project
              </span>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-3">
          <h4 className="font-medium text-sm text-gray-700">Features</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {Object.entries(subscription.features || {}).map(([feature, enabled]) => (
              <div key={feature} className="flex items-center space-x-2">
                {enabled ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <XCircle className="h-4 w-4 text-gray-300" />
                )}
                <span className={`text-sm ${enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                  {feature.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Billing Info */}
        {isPro && subscription.current_period_end && (
          <div className="space-y-3 border-t pt-3">
            <h4 className="font-medium text-sm text-gray-700">Billing</h4>
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <span className="text-sm">
                Next billing: {new Date(subscription.current_period_end).toLocaleDateString()}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SubscriptionCard;
